import React, { FC, ReactNode, useCallback } from 'react';
import { connect, useDispatch } from 'react-redux';
import classNames from 'classnames';

import type { ChangeHandler } from '../../types';
import type { FreeBet } from 'app/api/services/freebets/types';
import type { ReduxState } from 'app/redux/types';

import { Icon, IconSize } from 'app/design-system/components/Icon';
import { Hint } from 'app/design-system/components/Popper';
import { StakeType } from 'app/lib/staking/constants';

import { setFreeBet } from '../../../../actions/paymentOptions';
import { setExpressFreeBet } from '../../../../actions/stakeTypes/express';
import { AccountType } from '../../../../constants';
import { selectCurrentStakeType } from '../../../../selectors/stakeTypes';

import styles from '../../FooterBar.module.scss';

export type ExtendedFreeBet = FreeBet & {
  disabled: boolean;
  disabledTitle: ReactNode;
};

type FreebetProps = {
  betId?: number;
  freebet: ExtendedFreeBet;
  onChange: ChangeHandler;
};

type StoreProps = {
  isExpress: boolean;
};

const Freebet: FC<FreebetProps & StoreProps> = ({
  betId,
  freebet,
  isExpress,
  onChange,
}) => {
  const dispatch = useDispatch();

  const setFreeBetHandler = () => {
    if (isExpress) {
      return dispatch(setExpressFreeBet(freebet));
    }
    if (!betId) {
      console.error(`BetSlip -> EntryFooterBar -> Freebet -> betId not found = ${betId}`);
      return;
    }

    dispatch(setFreeBet(betId, freebet));
  };

  const freebetSelectHandler = useCallback(() => {
    onChange({
      value: freebet.amount.toString(),
      account: AccountType.FREE_BET_ACCOUNT,
    });

    setFreeBetHandler();
  }, [onChange, setFreeBetHandler]);

  const icon = (
    <Icon icon="freebet"
          size={IconSize.SMALL} />
  );

  if (freebet.disabled) {
    return (
      <Hint key={freebet.id}
            content={freebet.disabledTitle}
            childrenWrapperClassName={classNames(styles.barButton, styles.freebetDisabled)}
            placement="bottom">
        {icon}
        {freebet.amount}
      </Hint>
    );
  }

  return (
    <button type="button"
            key={freebet.id}
            onClick={freebetSelectHandler}
            className={classNames(styles.barButton, styles.freebet)} >
      {icon}
      {freebet.amount}
    </button>
  );
};

const mapStateToProps = (state: ReduxState): StoreProps => ({
  isExpress: selectCurrentStakeType(state).has(StakeType.EXPRESS),
});

export default connect(mapStateToProps)(Freebet);
